import { useState, useEffect } from 'react'
import { Modal, Button, Typography, Space, Alert } from 'antd'
import { ClockCircleOutlined, LogoutOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { idleDetectionService } from '../services/idleDetectionService'

const { Text } = Typography

/**
 * Idle Timeout Warning Modal
 *
 * Displays warning 5 minutes before session timeout due to inactivity.
 * Automatically dismisses when user shows activity.
 *
 * Features:
 * - Shows idle duration and time until logout
 * - "Stay Logged In" button resets idle timer
 * - Auto-dismisses when user resumes activity
 * - Logout occurs automatically at timeout threshold
 */
const IdleTimeoutWarning: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [idleMinutes, setIdleMinutes] = useState(0)
  const [remainingMinutes, setRemainingMinutes] = useState(5)

  useEffect(() => {
    // Listen for idle events from detection service
    const unsubscribe = idleDetectionService.onIdleEvent((eventType, idleDuration) => {
      const minutes = Math.floor(idleDuration / 1000 / 60)

      if (eventType === 'warning') {
        // Show warning modal
        setIdleMinutes(minutes)
        setRemainingMinutes(5) // 5 minutes until timeout
        setIsVisible(true)
        console.log(`⚠️  Idle timeout warning shown: ${minutes} minutes idle`)
      } else if (eventType === 'timeout') {
        // Timeout occurred - modal will be dismissed as user is logged out
        setIsVisible(false)
        console.log(`⏰ Idle timeout occurred: ${minutes} minutes idle - logging out`)
      }
    })

    // Update countdown timer every 10 seconds
    const countdownInterval = setInterval(() => {
      if (isVisible) {
        const currentIdleDuration = idleDetectionService.getIdleDuration()
        const currentIdleMinutes = Math.floor(currentIdleDuration / 1000 / 60)
        const timeoutMinutes = idleDetectionService.getTimeoutMinutes()
        const remaining = Math.max(0, timeoutMinutes - currentIdleMinutes)

        setIdleMinutes(currentIdleMinutes)
        setRemainingMinutes(remaining)

        // Auto-dismiss if user became active (remaining increased significantly)
        if (remaining > 5) {
          setIsVisible(false)
          console.log('✅ User became active - dismissing warning')
        }
      }
    }, 10000) // Update every 10 seconds

    return () => {
      unsubscribe()
      clearInterval(countdownInterval)
    }
  }, [isVisible])

  const handleStayLoggedIn = () => {
    console.log('🔄 User clicked "Stay Logged In" - resetting idle timer')
    idleDetectionService.resetActivity()
    setIsVisible(false)
  }

  const formatIdleTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours} hour${hours !== 1 ? 's' : ''} and ${mins} minute${mins !== 1 ? 's' : ''}`
  }

  return (
    <Modal
      open={isVisible}
      title={
        <Space>
          <ClockCircleOutlined style={{ color: '#faad14' }} />
          <span>Session Timeout Warning</span>
        </Space>
      }
      onCancel={handleStayLoggedIn}
      footer={[
        <Button
          key="stay"
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleStayLoggedIn}
          size="large"
        >
          Stay Logged In
        </Button>
      ]}
      closable={true}
      maskClosable={false}
      width={500}
      centered
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message="Inactivity Detected"
          description={
            <div>
              <Text>
                You've been idle for <Text strong>{formatIdleTime(idleMinutes)}</Text>.
              </Text>
              <br />
              <Text>
                You'll be automatically logged out in approximately{' '}
                <Text strong type="warning">{remainingMinutes} minute{remainingMinutes !== 1 ? 's' : ''}</Text>
                {' '}for security.
              </Text>
            </div>
          }
          type="warning"
          showIcon
          icon={<LogoutOutlined />}
        />

        <div style={{
          padding: '16px',
          background: '#fafafa',
          borderRadius: '4px',
          border: '1px solid #d9d9d9'
        }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <ClockCircleOutlined /> Session timeout is set to {formatIdleTime(idleDetectionService.getTimeoutMinutes())} of inactivity
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <CheckCircleOutlined /> Click "Stay Logged In" or perform any action to continue
            </Text>
          </Space>
        </div>
      </Space>
    </Modal>
  )
}

export default IdleTimeoutWarning
